import { useState, useCallback } from 'react'
import type { Camera, CameraEvent, EventType } from '../../types'
import { useToast } from '../ui/Toast'
import { usePreferences } from '../../hooks/usePreferences'
import { supabase } from '../../lib/supabase'
import {
  queryRecordings,
  downloadRecording,
  parseCameraTs,
  type RecordingSegment,
} from '../../lib/cameraApi'

interface Props {
  camera: Camera
  onClose: () => void
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return m > 0 ? `${m}m ${s}s` : `${s}s`
}

function toLocalDatetime(d: Date): string {
  const pad = (n: number) => n.toString().padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

const EVENT_COLORS: Record<EventType, string> = {
  face: '#8b5cf6',
  body: '#3b82f6',
  license_plate: '#f59e0b',
  vehicle: '#ef4444',
  motion: '#6b7280',
  intrusion: '#dc2626',
  line_cross: '#06b6d4',
  enter_area: '#10b981',
  leave_area: '#f97316',
}

const EVENT_LABELS: Record<EventType, string> = {
  face: 'Face',
  body: 'Person',
  license_plate: 'Plate',
  vehicle: 'Vehicle',
  motion: 'Motion',
  intrusion: 'Intrusion',
  line_cross: 'Line Cross',
  enter_area: 'Enter',
  leave_area: 'Leave',
}

export function RecordingPanel({ camera, onClose }: Props) {
  const { toast } = useToast()
  const { formatTime } = usePreferences()
  const fmtTs = (ts: string) => formatTime(parseCameraTs(ts))
  const now = new Date()
  const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000)

  const [startStr, setStartStr] = useState(toLocalDatetime(oneHourAgo))
  const [endStr, setEndStr] = useState(toLocalDatetime(now))
  const [segments, setSegments] = useState<RecordingSegment[]>([])
  const [events, setEvents] = useState<CameraEvent[]>([])
  const [searching, setSearching] = useState(false)
  const [searched, setSearched] = useState(false)
  const [downloading, setDownloading] = useState<string | null>(null)
  const [eventFilter, setEventFilter] = useState<EventType | 'all'>('all')
  const [showEvents, setShowEvents] = useState(true)

  // Load events for this camera within the time range
  const loadEvents = useCallback(async (start: Date, end: Date) => {
    const { data } = await supabase
      .from('camera_events')
      .select('*')
      .eq('camera_id', camera.id)
      .gte('timestamp', start.toISOString())
      .lte('timestamp', end.toISOString())
      .order('timestamp', { ascending: true })
      .limit(500)
    setEvents((data ?? []) as CameraEvent[])
  }, [camera.id])

  const handleSearch = useCallback(async () => {
    const start = new Date(startStr)
    const end = new Date(endStr)
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      toast('Invalid date range', 'error')
      return
    }
    if (start >= end) {
      toast('Start must be before end', 'error')
      return
    }

    setSearching(true)
    setSearched(false)
    try {
      const [results] = await Promise.all([
        queryRecordings(camera.id, start, end),
        loadEvents(start, end),
      ])
      setSegments(results)
      setSearched(true)
      if (results.length === 0) toast('No recordings found', 'info')
    } catch {
      toast('Failed to query recordings', 'error')
    } finally {
      setSearching(false)
    }
  }, [camera.id, startStr, endStr, toast, loadEvents])

  const handleDownload = useCallback(async (seg: RecordingSegment) => {
    setDownloading(seg.startTime)
    try {
      await downloadRecording(camera.id, camera.name, seg.startTime, seg.endTime)
      toast('Download started', 'success')
    } catch {
      toast('Download failed', 'error')
    } finally {
      setDownloading(null)
    }
  }, [camera.id, camera.name, toast])

  const inputCls = 'w-full bg-gray-50 border border-gray-200 rounded-md px-2 py-1.5 text-gray-900 text-xs focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand/20'
  const labelCls = 'block text-[10px] uppercase tracking-wide text-gray-500 mb-1'
  const btnBase = 'text-xs px-2.5 py-1.5 rounded transition-colors font-medium'
  const btnDefault = `${btnBase} bg-gray-100 text-gray-600 hover:text-gray-900 hover:bg-gray-200`

  // Timeline bar
  const startDate = new Date(startStr)
  const endDate = new Date(endStr)
  const rangeMs = endDate.getTime() - startDate.getTime()

  // Filter events
  const filteredEvents = eventFilter === 'all'
    ? events
    : events.filter(e => e.event_type === eventFilter)

  // Count events by type
  const eventCounts = events.reduce<Partial<Record<EventType, number>>>((acc, e) => {
    acc[e.event_type as EventType] = (acc[e.event_type as EventType] ?? 0) + 1
    return acc
  }, {})

  return (
    <div className="fixed top-0 right-0 bottom-0 w-80 bg-sidebar border-l border-card-border overflow-y-auto z-10">
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-card-border">
        <div>
          <p className="text-sm font-medium">{camera.name}</p>
          <p className="text-xs text-gray-500">Recordings & Events</p>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-700 text-lg leading-none transition-colors"
        >
          &times;
        </button>
      </div>

      <div className="p-3 space-y-4">
        {/* Date range */}
        <div>
          <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-2">Time Range</p>
          <div className="space-y-2">
            <div>
              <label className={labelCls}>From</label>
              <input
                type="datetime-local"
                value={startStr}
                onChange={(e) => setStartStr(e.target.value)}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>To</label>
              <input
                type="datetime-local"
                value={endStr}
                onChange={(e) => setEndStr(e.target.value)}
                className={inputCls}
              />
            </div>
          </div>
          <button
            onClick={handleSearch}
            disabled={searching}
            className="w-full mt-2 bg-brand hover:bg-brand-dark text-white text-xs font-medium py-2 rounded-md transition-colors disabled:opacity-50"
          >
            {searching ? 'Searching...' : 'Search'}
          </button>
        </div>

        {/* Timeline bar with recordings + events */}
        {searched && segments.length > 0 && rangeMs > 0 && (
          <div>
            <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Timeline</p>
            {/* Recordings bar */}
            <div className="relative h-4 bg-gray-100 rounded overflow-hidden">
              {segments.map((seg, i) => {
                const segStart = parseCameraTs(seg.startTime).getTime()
                const segEnd = parseCameraTs(seg.endTime).getTime()
                const left = Math.max(0, ((segStart - startDate.getTime()) / rangeMs) * 100)
                const width = Math.min(100 - left, ((segEnd - segStart) / rangeMs) * 100)
                return (
                  <div
                    key={i}
                    className="absolute top-0 bottom-0 bg-brand/70 rounded-sm"
                    style={{ left: `${left}%`, width: `${Math.max(width, 0.5)}%` }}
                    title={`${fmtTs(seg.startTime)} - ${fmtTs(seg.endTime)}`}
                  />
                )
              })}
            </div>

            {/* Events bar */}
            {showEvents && filteredEvents.length > 0 && (
              <div className="relative h-3 bg-gray-50 rounded overflow-hidden mt-1">
                {filteredEvents.map((evt) => {
                  const evtTime = new Date(evt.timestamp).getTime()
                  const left = Math.max(0, ((evtTime - startDate.getTime()) / rangeMs) * 100)
                  const color = EVENT_COLORS[evt.event_type as EventType] ?? '#6b7280'
                  return (
                    <div
                      key={evt.id}
                      className="absolute top-0 bottom-0 rounded-full"
                      style={{
                        left: `${left}%`,
                        width: '4px',
                        backgroundColor: color,
                        transform: 'translateX(-2px)',
                      }}
                      title={`${EVENT_LABELS[evt.event_type as EventType] ?? evt.event_type} — ${formatTime(evt.timestamp)}`}
                    />
                  )
                })}
              </div>
            )}

            <div className="flex justify-between mt-0.5">
              <span className="text-[9px] text-gray-600">{fmtTs(segments[0]?.startTime ?? '')}</span>
              <span className="text-[9px] text-gray-600">{fmtTs(segments[segments.length - 1]?.endTime ?? '')}</span>
            </div>
          </div>
        )}

        {/* Event filter chips */}
        {searched && events.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-[10px] uppercase tracking-wider text-gray-500">Events ({events.length})</p>
              <button
                onClick={() => setShowEvents(!showEvents)}
                className="text-[10px] text-brand hover:underline"
              >
                {showEvents ? 'Hide' : 'Show'}
              </button>
            </div>
            {showEvents && (
              <>
                <div className="flex flex-wrap gap-1 mb-2">
                  <button
                    onClick={() => setEventFilter('all')}
                    className={`text-[10px] px-2 py-0.5 rounded-full transition-colors ${
                      eventFilter === 'all'
                        ? 'bg-gray-700 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    All
                  </button>
                  {(Object.keys(eventCounts) as EventType[]).map(type => (
                    <button
                      key={type}
                      onClick={() => setEventFilter(type)}
                      className="text-[10px] px-2 py-0.5 rounded-full transition-colors"
                      style={{
                        backgroundColor: eventFilter === type ? EVENT_COLORS[type] : undefined,
                        color: eventFilter === type ? 'white' : EVENT_COLORS[type],
                        border: `1px solid ${EVENT_COLORS[type]}`,
                      }}
                    >
                      {EVENT_LABELS[type]} ({eventCounts[type]})
                    </button>
                  ))}
                </div>

                {/* Event list */}
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {filteredEvents.slice(0, 50).map((evt) => (
                    <div
                      key={evt.id}
                      className="flex items-center gap-2 bg-gray-50 rounded px-2 py-1"
                    >
                      <div
                        className="w-2 h-2 rounded-full shrink-0"
                        style={{ backgroundColor: EVENT_COLORS[evt.event_type as EventType] }}
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-[10px] text-gray-700 truncate">
                          <span className="font-medium">{EVENT_LABELS[evt.event_type as EventType]}</span>
                          {evt.confidence ? ` ${evt.confidence}%` : ''}
                          {evt.metadata && (evt.metadata as Record<string, unknown>).plate_number
                            ? ` — ${(evt.metadata as Record<string, unknown>).plate_number}`
                            : ''}
                        </p>
                        <p className="text-[9px] text-gray-500">
                          {formatTime(evt.timestamp)}
                        </p>
                      </div>
                    </div>
                  ))}
                  {filteredEvents.length > 50 && (
                    <p className="text-[9px] text-gray-400 text-center">
                      +{filteredEvents.length - 50} more
                    </p>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* Recording segments list */}
        {searched && (
          <div>
            <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-2">
              {segments.length > 0 ? `${segments.length} Segment${segments.length > 1 ? 's' : ''}` : 'No Recordings'}
            </p>
            <div className="space-y-1 max-h-80 overflow-y-auto">
              {segments.map((seg) => (
                <div
                  key={seg.startTime}
                  className="flex items-center justify-between bg-gray-50 rounded px-2 py-1.5"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-gray-700 truncate">
                      {fmtTs(seg.startTime)} - {fmtTs(seg.endTime)}
                    </p>
                    <p className="text-[10px] text-gray-600">{formatDuration(seg.duration)}</p>
                  </div>
                  <button
                    className={btnDefault + ' ml-2 shrink-0'}
                    onClick={() => handleDownload(seg)}
                    disabled={downloading === seg.startTime}
                  >
                    {downloading === seg.startTime ? '...' : 'DL'}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
